/**
 * VoiceRecorder — V1.8.5
 *
 * Tap-and-hold on the mic button to record via MediaRecorder API.
 * Release to send, drag-off to cancel (WhatsApp behavior).
 *
 * Props:
 *   - onSend(blob, durationMs)  → invoked when the user releases after
 *     recording ≥ MIN_MS. The parent handles the multipart POST.
 *   - disabled                  → grey the button out.
 *   - onError(err)              → non-fatal UI toast hook.
 *   - testId
 *
 * The recorder picks the first supported MIME. Safari lacks audio/webm
 * but supports audio/mp4; Chrome/Firefox emit audio/webm;codecs=opus.
 */
import React from "react";
import { Microphone, X, PaperPlaneTilt } from "@phosphor-icons/react";

const MIN_RECORDING_MS = 500;
const MAX_RECORDING_MS = 120_000;

function pickMimeType() {
    if (typeof MediaRecorder === "undefined") return null;
    const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
        "audio/mp4",
    ];
    for (const m of candidates) {
        try {
            if (MediaRecorder.isTypeSupported(m)) return m;
        } catch { /* ignore */ }
    }
    return null;
}

export function VoiceRecorder({ onSend, disabled, onError, testId = "voice-record-btn" }) {
    const [recording, setRecording] = React.useState(false);
    const [cancelIntent, setCancelIntent] = React.useState(false);
    const [elapsed, setElapsed] = React.useState(0);
    const recorderRef = React.useRef(null);
    const chunksRef = React.useRef([]);
    const startedAtRef = React.useRef(0);
    const streamRef = React.useRef(null);
    const cancelIntentRef = React.useRef(false);
    const tickRef = React.useRef(null);
    const maxTimerRef = React.useRef(null);

    const cleanup = React.useCallback(() => {
        clearInterval(tickRef.current);
        clearTimeout(maxTimerRef.current);
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
        streamRef.current = null;
        recorderRef.current = null;
        chunksRef.current = [];
        setRecording(false);
        setCancelIntent(false);
        setElapsed(0);
        cancelIntentRef.current = false;
    }, []);

    const startRecording = React.useCallback(async () => {
        if (disabled || recording) return;
        const mime = pickMimeType();
        if (!mime) {
            onError?.("Voice notes aren't supported on this browser.");
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mr = new MediaRecorder(stream, { mimeType: mime });
            chunksRef.current = [];
            mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
            mr.onstop = () => {
                const duration = Date.now() - startedAtRef.current;
                const blob = new Blob(chunksRef.current, { type: mime });
                const wasCancel = cancelIntentRef.current;
                cleanup();
                if (wasCancel) return;
                if (duration < MIN_RECORDING_MS || blob.size === 0) {
                    onError?.("Hold to record. Release to send.");
                    return;
                }
                onSend(blob, duration);
            };
            recorderRef.current = mr;
            startedAtRef.current = Date.now();
            mr.start();
            setRecording(true);
            setElapsed(0);
            tickRef.current = setInterval(() => {
                setElapsed(Date.now() - startedAtRef.current);
            }, 200);
            maxTimerRef.current = setTimeout(() => {
                if (recorderRef.current && recorderRef.current.state === "recording") {
                    recorderRef.current.stop();
                }
            }, MAX_RECORDING_MS);
        } catch (err) {
            onError?.(err?.name === "NotAllowedError"
                ? "Microphone permission denied."
                : "Couldn't start recording.");
            cleanup();
        }
    }, [disabled, recording, onSend, onError, cleanup]);

    const stopRecording = React.useCallback((cancel = false) => {
        if (!recording || !recorderRef.current) return;
        cancelIntentRef.current = cancel;
        try {
            if (recorderRef.current.state === "recording") {
                recorderRef.current.stop();
            }
        } catch { /* ignore */ }
    }, [recording]);

    // Global mouse-up so releasing outside the button still ends recording.
    React.useEffect(() => {
        if (!recording) return;
        const onUp = () => stopRecording(cancelIntentRef.current);
        window.addEventListener("mouseup", onUp);
        window.addEventListener("touchend", onUp);
        return () => {
            window.removeEventListener("mouseup", onUp);
            window.removeEventListener("touchend", onUp);
        };
    }, [recording, stopRecording]);

    React.useEffect(() => () => cleanup(), [cleanup]);

    const secs = Math.floor(elapsed / 1000);
    const mm = String(Math.floor(secs / 60)).padStart(1, "0");
    const ss = String(secs % 60).padStart(2, "0");

    return (
        <>
            <button
                type="button"
                onMouseDown={startRecording}
                onTouchStart={startRecording}
                onContextMenu={(e) => e.preventDefault()}
                disabled={disabled}
                aria-label="Hold to record voice note"
                data-testid={testId}
                className={`inline-flex items-center justify-center w-10 h-10 rounded-full shrink-0 transition
                    ${recording
                        ? "bg-red-500 text-white shadow-lg scale-110"
                        : "bg-white text-cobalt ring-1 ring-cobalt/30 hover:ring-cobalt/60"}
                    disabled:opacity-40 disabled:cursor-not-allowed`}
            >
                <Microphone size={18} weight="fill" />
            </button>

            {recording && (
                <div
                    data-testid="voice-record-overlay"
                    className="fixed inset-x-4 bottom-24 z-50 mx-auto max-w-md bg-white rounded-2xl shadow-2xl ring-1 ring-ink/10 p-4"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                        <span className="text-sm font-semibold tabular-nums" data-testid="voice-record-duration">
                            {mm}:{ss}
                        </span>
                        <span className="text-xs text-ink/60 flex-1">
                            {cancelIntent ? "Release to cancel" : "Release to send · swipe up to cancel"}
                        </span>
                        <button
                            type="button"
                            onMouseEnter={() => { setCancelIntent(true); cancelIntentRef.current = true; }}
                            onMouseLeave={() => { setCancelIntent(false); cancelIntentRef.current = false; }}
                            onClick={() => stopRecording(true)}
                            data-testid="voice-record-cancel"
                            className="w-8 h-8 rounded-full bg-ink/5 hover:bg-red-100 text-ink flex items-center justify-center"
                        >
                            <X size={14} weight="bold" />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}

/**
 * VoicePlayer — playback bubble with play/pause + duration + tiny bar.
 *
 * Fetches the audio via authenticated axios (`api`) so cookies attach on
 * cross-origin requests. Builds a blob URL for the <audio> tag.
 */
export function VoicePlayer({ src, durationMs, isMine, transcript, testId = "voice-player" }) {
    const audioRef = React.useRef(null);
    const [playing, setPlaying] = React.useState(false);
    const [progress, setProgress] = React.useState(0);

    const toggle = () => {
        const el = audioRef.current;
        if (!el) return;
        if (playing) { el.pause(); } else { el.play().catch(() => { /* browser blocked */ }); }
    };

    React.useEffect(() => {
        const el = audioRef.current;
        if (!el) return;
        const onTime = () => {
            if (el.duration && Number.isFinite(el.duration)) {
                setProgress(el.currentTime / el.duration);
            }
        };
        const onPlay = () => setPlaying(true);
        const onPause = () => setPlaying(false);
        const onEnded = () => { setPlaying(false); setProgress(0); };
        el.addEventListener("timeupdate", onTime);
        el.addEventListener("play", onPlay);
        el.addEventListener("pause", onPause);
        el.addEventListener("ended", onEnded);
        return () => {
            el.removeEventListener("timeupdate", onTime);
            el.removeEventListener("play", onPlay);
            el.removeEventListener("pause", onPause);
            el.removeEventListener("ended", onEnded);
        };
    }, []);

    const totalSecs = Math.max(1, Math.floor((durationMs || 0) / 1000));
    const mm = Math.floor(totalSecs / 60);
    const ss = String(totalSecs % 60).padStart(2, "0");

    return (
        <div className="flex flex-col gap-1.5">
            <div
                data-testid={testId}
                className={`inline-flex items-center gap-2.5 px-3 py-2 rounded-full min-w-[180px]
                    ${isMine ? "bg-cobalt/15" : "bg-ink/5"}`}
            >
                <audio ref={audioRef} src={src} preload="metadata" />
                <button
                    type="button"
                    onClick={toggle}
                    data-testid={`${testId}-toggle`}
                    className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0
                        ${isMine ? "bg-cobalt text-white" : "bg-cobalt/90 text-white"}`}
                >
                    {playing
                        ? <PauseIcon />
                        : <PaperPlaneTilt size={0} className="hidden" /> || <PlayIcon />}
                </button>
                <div className="flex-1 flex items-center gap-0.5 h-5">
                    {Array.from({ length: 22 }).map((_, i) => {
                        const on = i / 22 <= progress;
                        const h = 4 + ((i * 37) % 12);
                        return (
                            <span
                                key={i}
                                className={`w-0.5 rounded-full ${on ? (isMine ? "bg-cobalt" : "bg-cobalt/80") : "bg-ink/25"}`}
                                style={{ height: `${h}px` }}
                            />
                        );
                    })}
                </div>
                <span className="text-[11px] tabular-nums text-ink/60 shrink-0">
                    {mm}:{ss}
                </span>
            </div>
            {/* V1.8.6 — transcript below the waveform. `undefined` = still
                processing; empty string = we tried and it produced nothing.
                V1.9.27 — use ink-toned color even on mine bubbles because
                the light-green surface makes white/85 illegible. */}
            {transcript === undefined ? (
                <span
                    data-testid={`${testId}-transcript-pending`}
                    className="text-[11px] italic text-ink/50 flex items-center gap-1"
                >
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                    Transcribing…
                </span>
            ) : transcript ? (
                <p
                    data-testid={`${testId}-transcript`}
                    className="text-[12px] leading-snug italic text-ink/70"
                >
                    “{transcript}”
                </p>
            ) : null}
        </div>
    );
}

function PlayIcon() {
    return <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><path d="M2.5 1.5v9L10 6z" /></svg>;
}
function PauseIcon() {
    return <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><rect x="2.5" y="1.5" width="2.5" height="9" /><rect x="7" y="1.5" width="2.5" height="9" /></svg>;
}
